import { useEffect, useId, useState, type FormEvent } from "react";
import { toast } from "sonner";

import {
  useBlockAccounts,
  useBlockAccountsExportSummary,
  useExportConfig,
  useRunDailyExport,
  useSetExportConfig,
  type BlockAccountDto,
  type RunDailyExportResult,
  type SubManagedAccountDto,
} from "@/hooks/useBlockAccounts";
import { useTenants, type Tenant } from "@/hooks/useTenants";
import type { Fcm, SmaStatus } from "@/lib/api";

const DEFAULT_EXPORT_TIMEZONE = "America/Chicago";

/**
 * SFWL-013 post-lock amendment: recipients paste from Outlook uses
 * semicolons; other sources use commas or newlines. Split on any run of
 * whitespace/comma/semicolon, trim, drop empties, and dedupe
 * case-insensitively while keeping the first-seen casing.
 */
function parseRecipients(input: string): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of input.split(/[\s,;]+/)) {
    const trimmed = raw.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(trimmed);
  }
  return result;
}

// Light client-side shape check, not full RFC 5322 validation — the server
// is the source of truth for what's actually deliverable.
const EMAIL_SHAPE_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function summarizeResults(results: RunDailyExportResult[]): string {
  return results
    .map(
      (r) =>
        `${r.status} · ${r.rowCount} row${r.rowCount === 1 ? "" : "s"} (${r.attempts} attempt${r.attempts === 1 ? "" : "s"})`
    )
    .join("; ");
}

/**
 * SFWL-012: internal super-admin seeding/demo page for block accounts + SMAs.
 * Not in nav — direct-link only (`/admin/block-accounts`). The server's
 * `SovrunAdminGuard` is the access-control gate; this page does no
 * client-side role checks and simply surfaces a 403 via toast + inline text
 * if a non-admin session hits it.
 */

const FCM_OPTIONS: Fcm[] = ["marex", "stonex", "other"];
const SMA_STATUS_OPTIONS: SmaStatus[] = ["pending", "active", "closed"];

interface CreateBlockFormValues {
  tenantId: string;
  fcm: Fcm;
  fcmAccountNumber: string;
  marginBalance?: number;
}

interface CreateSmaFormValues {
  customerName: string;
  accountNumber: string;
  subscribedQuantity: number;
  letterOfDirectionUrl?: string;
}

export default function AdminBlockAccounts() {
  const {
    blocks,
    isLoading,
    error,
    createBlock,
    deleteBlock,
    addSma,
    patchSma,
    deleteSma,
    recompute,
    linkAccount,
    isCreatingBlock,
    isDeletingBlock,
    isAddingSma,
    isPatchingSma,
    isDeletingSma,
    isRecomputing,
    isLinkingAccount,
  } = useBlockAccounts();

  const {
    rows: exportRows,
    isLoading: isExportLoading,
    error: exportError,
  } = useBlockAccountsExportSummary();

  const { data: tenantsData } = useTenants();
  const tenants = tenantsData?.tenants ?? [];

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Query errors (including the server's 403 for non-super-admins) surface as
  // a toast in addition to the inline message below — a side effect, not a
  // setState-in-effect, so it's safe to fire on every `error` change.
  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error]);

  useEffect(() => {
    if (exportError) toast.error(exportError.message);
  }, [exportError]);

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleCreateBlock = async (data: CreateBlockFormValues): Promise<boolean> => {
    try {
      await createBlock(data);
      toast.success("Block account created.");
      return true;
    } catch (e) {
      toast.error((e as Error).message);
      return false;
    }
  };

  const handleDeleteBlock = async (blockId: string) => {
    try {
      await deleteBlock(blockId);
      toast.success("Block account deleted.");
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  // AC1: deleting a block cascade-deletes every SMA under it and drops the
  // block from future exports — a single misclick is destructive, so this
  // internal admin page gates it behind a native confirm naming the block and
  // its SMA count. No new modal component per the locked spec's "minimal" FE.
  const confirmDeleteBlock = (block: BlockAccountDto) => {
    const smaCount = block.smas?.length ?? 0;
    const confirmed = window.confirm(
      `Delete block account ${block.fcmAccountNumber}? This will cascade-delete ${smaCount} SMA${
        smaCount === 1 ? "" : "s"
      } and drop this block's P&L from future exports.`
    );
    if (!confirmed) return;
    void handleDeleteBlock(block.id);
  };

  const handleRecompute = async (blockId: string) => {
    try {
      await recompute(blockId);
      toast.success("Recomputed.");
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const handleAddSma = async (
    blockId: string,
    data: CreateSmaFormValues
  ): Promise<boolean> => {
    try {
      await addSma({ blockId, data });
      toast.success("SMA added.");
      return true;
    } catch (e) {
      toast.error((e as Error).message);
      return false;
    }
  };

  const handlePatchSmaStatus = async (
    blockId: string,
    smaId: string,
    status: SmaStatus
  ) => {
    try {
      await patchSma({ blockId, smaId, data: { status } });
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const handleDeleteSma = async (blockId: string, smaId: string) => {
    try {
      await deleteSma({ blockId, smaId });
      toast.success("SMA removed.");
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const handleLinkAccount = async (
    blockId: string,
    smaId: string,
    paperTradingAccountId: string
  ): Promise<boolean> => {
    try {
      await linkAccount({ blockId, smaId, data: { paperTradingAccountId } });
      toast.success("Paper trading account linked.");
      return true;
    } catch (e) {
      toast.error((e as Error).message);
      return false;
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
      <title>Block Accounts | Sovrun</title>
      <h1 className="mb-2 text-2xl font-bold text-slate-900">Block Accounts</h1>
      <p className="mb-6 text-sm text-slate-500">
        Super-admin only. Manage FCM block accounts and their SMAs feeding the daily Sweet
        Futures export.
      </p>

      {error && (
        <p className="mb-4 text-sm text-red-600">
          Couldn&apos;t load block accounts: {error.message}
        </p>
      )}

      <CreateBlockForm
        tenants={tenants}
        onSubmit={handleCreateBlock}
        isSubmitting={isCreatingBlock}
      />

      {isLoading && (
        <p className="mt-6 text-sm text-slate-400">Loading block accounts…</p>
      )}

      {!isLoading && !error && (
        <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-medium uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2" />
                <th className="px-4 py-2">Tenant</th>
                <th className="px-4 py-2">FCM</th>
                <th className="px-4 py-2">Account #</th>
                <th className="px-4 py-2">Total Qty</th>
                <th className="px-4 py-2">Margin Balance</th>
                <th className="px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {blocks.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-slate-400 italic">
                    No block accounts yet.
                  </td>
                </tr>
              )}
              {blocks.map((block) => (
                <BlockRow
                  key={block.id}
                  block={block}
                  expanded={expandedIds.has(block.id)}
                  onToggle={() => toggleExpanded(block.id)}
                  onDelete={() => confirmDeleteBlock(block)}
                  onRecompute={() => handleRecompute(block.id)}
                  onAddSma={(data) => handleAddSma(block.id, data)}
                  onPatchSmaStatus={(smaId, status) =>
                    handlePatchSmaStatus(block.id, smaId, status)
                  }
                  onDeleteSma={(smaId) => handleDeleteSma(block.id, smaId)}
                  onLinkAccount={(smaId, accountId) =>
                    handleLinkAccount(block.id, smaId, accountId)
                  }
                  isRecomputing={isRecomputing}
                  isDeletingBlock={isDeletingBlock}
                  isAddingSma={isAddingSma}
                  isPatchingSma={isPatchingSma}
                  isDeletingSma={isDeletingSma}
                  isLinkingAccount={isLinkingAccount}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2 className="mt-10 mb-2 text-lg font-semibold text-slate-900">Export Preview</h2>
      <p className="mb-4 text-sm text-slate-500">
        Preview of the daily export row shape (#70 consumes this). Unrealized P&amp;L is
        reserved-null this iteration.
      </p>

      {exportError && (
        <p className="mb-4 text-sm text-red-600">
          Couldn&apos;t load export summary: {exportError.message}
        </p>
      )}

      {isExportLoading && (
        <p className="text-sm text-slate-400">Loading export summary…</p>
      )}

      {!isExportLoading && !exportError && (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-medium uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">Customer</th>
                <th className="px-4 py-2">Account #</th>
                <th className="px-4 py-2">Subscribed Qty</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Realized P&amp;L</th>
                <th className="px-4 py-2">Unrealized P&amp;L</th>
                <th className="px-4 py-2">Open Positions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {exportRows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-slate-400 italic">
                    No export rows yet.
                  </td>
                </tr>
              )}
              {exportRows.map((row) => (
                <tr key={row.smaId}>
                  <td className="px-4 py-2">{row.customerName}</td>
                  <td className="px-4 py-2">{row.accountNumber}</td>
                  <td className="px-4 py-2">{row.subscribedQuantity}</td>
                  <td className="px-4 py-2">{row.status}</td>
                  <td className="px-4 py-2">{row.realizedPnl}</td>
                  <td className="px-4 py-2">
                    {row.unrealizedPnl === null ? "—" : row.unrealizedPnl}
                  </td>
                  <td className="px-4 py-2">{row.openPositionCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <DailyExportSection tenants={tenants} />
    </div>
  );
}

function DailyExportSection({ tenants }: { tenants: Tenant[] }) {
  const idPrefix = useId();
  const [tenantId, setTenantId] = useState("");
  // `null` means "untouched" — the displayed value derives from the
  // selected tenant's query data. Once the user types, it holds the live
  // input value. This is the derive-from-props pattern (`override || !data`)
  // rather than a mount-time `useState(!data)`, which gets stuck on refetch.
  const [recipientsInput, setRecipientsInput] = useState<string | null>(null);
  const [timezoneInput, setTimezoneInput] = useState<string | null>(null);

  // Config now lives at the guarded export-config endpoint, not the tenant
  // PATCH (post-lock amendment — the tenant PATCH was unguarded and a #138
  // double-prefix offender). Only fetch once a tenant is picked.
  const {
    config,
    isLoading: isConfigLoading,
    error: configError,
  } = useExportConfig(tenantId || undefined);
  const { mutateAsync: setExportConfig, isPending: isSaving } = useSetExportConfig();
  const { mutateAsync: runDailyExport, isPending: isRunningExport } = useRunDailyExport();

  // Block Save until the selected tenant's config has actually loaded — a
  // quick Save while the GET is pending (or failed) would derive the form
  // from the empty defaults and overwrite the tenant's real recipient list.
  // Gated on tenantId because the disabled query reports pending forever.
  // Run-now doesn't touch the config, so it stays enabled.
  const isConfigPending = !!tenantId && isConfigLoading;
  const isConfigUnavailable = isConfigPending || (!!tenantId && configError !== null);

  const recipientsValue =
    recipientsInput ?? (config?.dailyExportRecipients ?? []).join(", ");
  const timezoneValue =
    timezoneInput ?? config?.dailyExportTimezone ?? DEFAULT_EXPORT_TIMEZONE;

  const handleSelectTenant = (id: string) => {
    setTenantId(id);
    // Switching tenants drops any in-progress edits for the previous one —
    // the inputs re-derive from the newly selected tenant's query data.
    setRecipientsInput(null);
    setTimezoneInput(null);
  };

  const handleSave = async () => {
    if (!tenantId) {
      toast.error("Select a tenant.");
      return;
    }
    const recipients = parseRecipients(recipientsValue);
    const invalid = recipients.filter((r) => !EMAIL_SHAPE_RE.test(r));
    if (invalid.length > 0) {
      toast.error(`Invalid email(s): ${invalid.join(", ")}`);
      return;
    }
    const timezone = timezoneValue.trim() || DEFAULT_EXPORT_TIMEZONE;
    try {
      await setExportConfig({
        tenantId,
        data: { dailyExportRecipients: recipients, dailyExportTimezone: timezone },
      });
      toast.success("Daily export settings saved.");
      // useSetExportConfig's onSuccess already wrote the saved config into
      // the query cache, so clearing the override here re-derives from the
      // fresh cache value instead of flickering back to a stale one.
      setRecipientsInput(null);
      setTimezoneInput(null);
    } catch (e) {
      // Preserve the operator's input on error — nothing is cleared here.
      toast.error((e as Error).message);
    }
  };

  const handleRunNow = async () => {
    if (!tenantId) {
      toast.error("Select a tenant.");
      return;
    }
    try {
      const results = await runDailyExport(tenantId);
      if (results.length === 0) {
        toast.error("No export result returned.");
        return;
      }
      const anyFailed = results.some((r) => r.status === "failed");
      const summary = summarizeResults(results);
      if (anyFailed) {
        toast.error(`Export failed: ${summary}`);
      } else {
        toast.success(`Export sent: ${summary}`);
      }
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <div className="mt-10">
      <h2 className="mb-2 text-lg font-semibold text-slate-900">Daily Export</h2>
      <p className="mb-4 text-sm text-slate-500">
        Configure the daily Block/SMA CSV email for a tenant, or trigger it immediately
        for a demo.
      </p>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="mb-4">
          <label
            htmlFor={`${idPrefix}-export-tenant`}
            className="mb-1 block text-xs font-medium text-slate-600"
          >
            {/* Deliberately not "Tenant" — the create-block form above already
                owns that accessible name; a second exact match would make
                `getByLabelText(/tenant/i)` ambiguous in tests. */}
            Export target
          </label>
          <select
            id={`${idPrefix}-export-tenant`}
            value={tenantId}
            onChange={(e) => handleSelectTenant(e.target.value)}
            className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900"
          >
            <option value="">Select tenant…</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4 flex flex-wrap gap-3">
          <div className="min-w-[240px] flex-1">
            <label
              htmlFor={`${idPrefix}-recipients`}
              className="mb-1 block text-xs font-medium text-slate-600"
            >
              Recipients (comma, semicolon, or newline separated)
            </label>
            <textarea
              id={`${idPrefix}-recipients`}
              value={recipientsValue}
              onChange={(e) => setRecipientsInput(e.target.value)}
              disabled={!tenantId}
              rows={2}
              placeholder="ops@sweetfutures.com, fcm@marex.com"
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 disabled:opacity-50"
            />
          </div>
          <div>
            <label
              htmlFor={`${idPrefix}-timezone`}
              className="mb-1 block text-xs font-medium text-slate-600"
            >
              Timezone
            </label>
            <input
              id={`${idPrefix}-timezone`}
              value={timezoneValue}
              onChange={(e) => setTimezoneInput(e.target.value)}
              disabled={!tenantId}
              placeholder={DEFAULT_EXPORT_TIMEZONE}
              className="w-48 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 disabled:opacity-50"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={!tenantId || isSaving || isConfigUnavailable}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
          >
            {isSaving ? "Saving…" : isConfigPending ? "Loading…" : "Save"}
          </button>
          <button
            type="button"
            onClick={handleRunNow}
            disabled={!tenantId || isRunningExport}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
          >
            {isRunningExport ? "Running…" : "Run export now"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateBlockForm({
  tenants,
  onSubmit,
  isSubmitting,
}: {
  tenants: Tenant[];
  onSubmit: (data: CreateBlockFormValues) => Promise<boolean>;
  isSubmitting: boolean;
}) {
  const [tenantId, setTenantId] = useState("");
  const [fcm, setFcm] = useState<Fcm>("marex");
  const [fcmAccountNumber, setFcmAccountNumber] = useState("");
  const [marginBalance, setMarginBalance] = useState("");
  const idPrefix = useId();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenantId) {
      toast.error("Select a tenant.");
      return;
    }
    if (!fcmAccountNumber.trim()) {
      toast.error("FCM account number is required.");
      return;
    }
    const ok = await onSubmit({
      tenantId,
      fcm,
      fcmAccountNumber: fcmAccountNumber.trim(),
      marginBalance: marginBalance.trim() ? Number(marginBalance) : undefined,
    });
    // Only clear the operator's input on success — a 409/400/403 leaves the
    // typed values intact so they aren't forced to retype after an error.
    if (ok) {
      setTenantId("");
      setFcmAccountNumber("");
      setMarginBalance("");
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4"
    >
      <div>
        <label
          htmlFor={`${idPrefix}-tenant`}
          className="mb-1 block text-xs font-medium text-slate-600"
        >
          Tenant
        </label>
        <select
          id={`${idPrefix}-tenant`}
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
          className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900"
        >
          <option value="">Select tenant…</option>
          {tenants.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label
          htmlFor={`${idPrefix}-fcm`}
          className="mb-1 block text-xs font-medium text-slate-600"
        >
          FCM
        </label>
        <select
          id={`${idPrefix}-fcm`}
          value={fcm}
          onChange={(e) => setFcm(e.target.value as Fcm)}
          className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900"
        >
          {FCM_OPTIONS.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label
          htmlFor={`${idPrefix}-account-number`}
          className="mb-1 block text-xs font-medium text-slate-600"
        >
          FCM Account #
        </label>
        <input
          id={`${idPrefix}-account-number`}
          value={fcmAccountNumber}
          onChange={(e) => setFcmAccountNumber(e.target.value)}
          className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900"
        />
      </div>
      <div>
        <label
          htmlFor={`${idPrefix}-margin-balance`}
          className="mb-1 block text-xs font-medium text-slate-600"
        >
          Margin Balance
        </label>
        <input
          id={`${idPrefix}-margin-balance`}
          type="number"
          step="any"
          value={marginBalance}
          onChange={(e) => setMarginBalance(e.target.value)}
          className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900"
        />
      </div>
      <button
        type="submit"
        disabled={isSubmitting}
        className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
      >
        {isSubmitting ? "Creating…" : "Create Block"}
      </button>
    </form>
  );
}

function BlockRow({
  block,
  expanded,
  onToggle,
  onDelete,
  onRecompute,
  onAddSma,
  onPatchSmaStatus,
  onDeleteSma,
  onLinkAccount,
  isRecomputing,
  isDeletingBlock,
  isAddingSma,
  isPatchingSma,
  isDeletingSma,
  isLinkingAccount,
}: {
  block: BlockAccountDto;
  expanded: boolean;
  onToggle: () => void;
  onDelete: () => void;
  onRecompute: () => void;
  onAddSma: (data: CreateSmaFormValues) => Promise<boolean>;
  onPatchSmaStatus: (smaId: string, status: SmaStatus) => void;
  onDeleteSma: (smaId: string) => void;
  onLinkAccount: (smaId: string, paperTradingAccountId: string) => Promise<boolean>;
  isRecomputing: boolean;
  isDeletingBlock: boolean;
  isAddingSma: boolean;
  isPatchingSma: boolean;
  isDeletingSma: boolean;
  isLinkingAccount: boolean;
}) {
  return (
    <>
      <tr>
        <td className="px-4 py-2">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            aria-label={
              expanded
                ? `Collapse block ${block.fcmAccountNumber}`
                : `Expand block ${block.fcmAccountNumber}`
            }
            className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
          >
            {expanded ? "−" : "+"}
          </button>
        </td>
        <td className="px-4 py-2">{block.tenantId}</td>
        <td className="px-4 py-2 uppercase">{block.fcm}</td>
        <td className="px-4 py-2">{block.fcmAccountNumber}</td>
        <td className="px-4 py-2">{block.totalQuantity}</td>
        <td className="px-4 py-2">{block.marginBalance}</td>
        <td className="px-4 py-2">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onRecompute}
              disabled={isRecomputing}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
            >
              Recompute
            </button>
            <button
              type="button"
              onClick={onDelete}
              disabled={isDeletingBlock}
              className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 transition hover:bg-red-50 disabled:opacity-50"
            >
              Delete
            </button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} className="bg-slate-50 px-4 py-4">
            <SmaTable
              smas={block.smas ?? []}
              onPatchStatus={onPatchSmaStatus}
              onDelete={onDeleteSma}
              onLinkAccount={onLinkAccount}
              isPatchingSma={isPatchingSma}
              isDeletingSma={isDeletingSma}
              isLinkingAccount={isLinkingAccount}
            />
            <AddSmaForm onSubmit={onAddSma} isSubmitting={isAddingSma} />
          </td>
        </tr>
      )}
    </>
  );
}

function SmaTable({
  smas,
  onPatchStatus,
  onDelete,
  onLinkAccount,
  isPatchingSma,
  isDeletingSma,
  isLinkingAccount,
}: {
  smas: SubManagedAccountDto[];
  onPatchStatus: (smaId: string, status: SmaStatus) => void;
  onDelete: (smaId: string) => void;
  onLinkAccount: (smaId: string, paperTradingAccountId: string) => Promise<boolean>;
  isPatchingSma: boolean;
  isDeletingSma: boolean;
  isLinkingAccount: boolean;
}) {
  if (smas.length === 0) {
    return <p className="mb-3 text-sm text-slate-400 italic">No SMAs yet.</p>;
  }

  return (
    <table className="mb-4 min-w-full divide-y divide-slate-200 text-sm">
      <thead className="text-left text-xs font-medium uppercase text-slate-500">
        <tr>
          <th className="px-3 py-1.5">Customer</th>
          <th className="px-3 py-1.5">Account #</th>
          <th className="px-3 py-1.5">Subscribed Qty</th>
          <th className="px-3 py-1.5">Status</th>
          <th className="px-3 py-1.5">LOD</th>
          <th className="px-3 py-1.5">Link Account</th>
          <th className="px-3 py-1.5" />
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {smas.map((sma) => (
          <SmaRow
            key={sma.id}
            sma={sma}
            onPatchStatus={(status) => onPatchStatus(sma.id, status)}
            onDelete={() => onDelete(sma.id)}
            onLinkAccount={(accountId) => onLinkAccount(sma.id, accountId)}
            isPatchingSma={isPatchingSma}
            isDeletingSma={isDeletingSma}
            isLinkingAccount={isLinkingAccount}
          />
        ))}
      </tbody>
    </table>
  );
}

function SmaRow({
  sma,
  onPatchStatus,
  onDelete,
  onLinkAccount,
  isPatchingSma,
  isDeletingSma,
  isLinkingAccount,
}: {
  sma: SubManagedAccountDto;
  onPatchStatus: (status: SmaStatus) => void;
  onDelete: () => void;
  onLinkAccount: (paperTradingAccountId: string) => Promise<boolean>;
  isPatchingSma: boolean;
  isDeletingSma: boolean;
  isLinkingAccount: boolean;
}) {
  const [linkValue, setLinkValue] = useState("");

  return (
    <tr>
      <td className="px-3 py-1.5">{sma.customerName}</td>
      <td className="px-3 py-1.5">{sma.accountNumber}</td>
      <td className="px-3 py-1.5">{sma.subscribedQuantity}</td>
      <td className="px-3 py-1.5">
        <select
          aria-label={`Status for ${sma.customerName}`}
          value={sma.status}
          disabled={isPatchingSma}
          onChange={(e) => onPatchStatus(e.target.value as SmaStatus)}
          className="rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-900 disabled:opacity-50"
        >
          {SMA_STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </td>
      <td className="px-3 py-1.5">
        {sma.letterOfDirectionUrl ? (
          <a
            href={sma.letterOfDirectionUrl}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-indigo-600 hover:underline"
          >
            LOD
          </a>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        )}
      </td>
      <td className="px-3 py-1.5">
        <div className="flex items-center gap-1.5">
          <input
            aria-label={`Paper trading account id for ${sma.customerName}`}
            value={linkValue}
            onChange={(e) => setLinkValue(e.target.value)}
            placeholder="Paper account ID"
            className="w-32 rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-900"
          />
          <button
            type="button"
            disabled={isLinkingAccount}
            onClick={async () => {
              if (!linkValue.trim()) {
                toast.error("Paper trading account ID is required.");
                return;
              }
              const ok = await onLinkAccount(linkValue.trim());
              if (ok) setLinkValue("");
            }}
            className="rounded border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
          >
            Link
          </button>
        </div>
      </td>
      <td className="px-3 py-1.5">
        <button
          type="button"
          disabled={isDeletingSma}
          onClick={onDelete}
          className="rounded border border-red-200 px-2 py-1 text-xs font-medium text-red-600 transition hover:bg-red-50 disabled:opacity-50"
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

function AddSmaForm({
  onSubmit,
  isSubmitting,
}: {
  onSubmit: (data: CreateSmaFormValues) => Promise<boolean>;
  isSubmitting: boolean;
}) {
  const [customerName, setCustomerName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [subscribedQuantity, setSubscribedQuantity] = useState("");
  const [letterOfDirectionUrl, setLetterOfDirectionUrl] = useState("");
  const idPrefix = useId();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!customerName.trim() || !accountNumber.trim()) {
      toast.error("Customer name and account number are required.");
      return;
    }
    const ok = await onSubmit({
      customerName: customerName.trim(),
      accountNumber: accountNumber.trim(),
      subscribedQuantity: subscribedQuantity.trim() ? Number(subscribedQuantity) : 0,
      letterOfDirectionUrl: letterOfDirectionUrl.trim() || undefined,
    });
    // Only clear on success — keep the operator's input on a server error.
    if (ok) {
      setCustomerName("");
      setAccountNumber("");
      setSubscribedQuantity("");
      setLetterOfDirectionUrl("");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-2">
      <div>
        <label
          htmlFor={`${idPrefix}-customer-name`}
          className="mb-1 block text-xs font-medium text-slate-600"
        >
          Customer Name
        </label>
        <input
          id={`${idPrefix}-customer-name`}
          value={customerName}
          onChange={(e) => setCustomerName(e.target.value)}
          className="rounded border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-900"
        />
      </div>
      <div>
        <label
          htmlFor={`${idPrefix}-account-number`}
          className="mb-1 block text-xs font-medium text-slate-600"
        >
          Account #
        </label>
        <input
          id={`${idPrefix}-account-number`}
          value={accountNumber}
          onChange={(e) => setAccountNumber(e.target.value)}
          className="rounded border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-900"
        />
      </div>
      <div>
        <label
          htmlFor={`${idPrefix}-subscribed-quantity`}
          className="mb-1 block text-xs font-medium text-slate-600"
        >
          Subscribed Qty
        </label>
        <input
          id={`${idPrefix}-subscribed-quantity`}
          type="number"
          step="any"
          value={subscribedQuantity}
          onChange={(e) => setSubscribedQuantity(e.target.value)}
          className="w-28 rounded border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-900"
        />
      </div>
      <div>
        <label
          htmlFor={`${idPrefix}-lod-url`}
          className="mb-1 block text-xs font-medium text-slate-600"
        >
          LOD URL
        </label>
        <input
          id={`${idPrefix}-lod-url`}
          value={letterOfDirectionUrl}
          onChange={(e) => setLetterOfDirectionUrl(e.target.value)}
          placeholder="https://…"
          className="w-40 rounded border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-900"
        />
      </div>
      <button
        type="submit"
        disabled={isSubmitting}
        className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
      >
        {isSubmitting ? "Adding…" : "Add SMA"}
      </button>
    </form>
  );
}
